<script setup lang="ts">
import type { SortableEvent } from 'sortablejs'
import type { CSSProperties } from 'vue'
import type { VxeTableInstance } from 'vxe-table'
import type {
  FaProTableOptions,
  ProTableBatchAction,
  ProTableColumn,
  ProTableContextMenuGroup,
  ProTableContextMenuItem,
  ProTableDensity,
  ProTableExportOptions,
  ProTableExpose,
  ProTableImportOptions,
  ProTablePagination,
  ProTablePermissionContext,
  ProTablePrintOptions,
  ProTableSettings,
  ProTableSpanMethod,
  ProTableSummaryMethod,
  ProTableTool,
  ProTableToolbarContext,
  ProTableToolbarPlugin,
  ProTableTreeProps,
  ProTableVirtualScrollAxisOptions,
} from './types'
import { useEventListener, useFullscreen } from '@vueuse/core'
import Sortable from 'sortablejs'
import VxeUITable from 'vxe-table'
import { useProTableRenderPlugin, useProTableToolbar } from '@/composables/business/useProTable'
import { defaultProTableDefaults, proTableDefaultsInjectionKey } from '@/config/proTable'
import FaProTableRecursiveColumn from './RecursiveColumn.vue'
import 'vxe-table/es/style.css'

defineOptions({
  name: 'FaProTable',
})

const props = withDefaults(defineProps<{
  options?: FaProTableOptions<any>
  columns: ProTableColumn<any>[]
  data: Record<string, any>[]
  loading?: boolean
  rowKey?: string | ((row: Record<string, any>) => string)
  pagination?: ProTablePagination | null
  tools?: ProTableTool[]
  storageKey?: string
  height?: string | number
  density?: ProTableDensity
  bordered?: boolean
  striped?: boolean
  showToolbar?: boolean
  showPagination?: boolean
  selection?: boolean
  indexColumn?: boolean
  batchActions?: ProTableBatchAction<any>[]
  toolbarPluginNames?: string[]
  selectionWidth?: number
  indexWidth?: number
  treeProps?: ProTableTreeProps
  lazy?: boolean
  load?: (row: Record<string, any>, treeNode: unknown, resolve: (data: Record<string, any>[]) => void) => void
  rowDraggable?: boolean
  rowDragHandle?: string
  rowContextMenu?: ((row: any) => ProTableContextMenuGroup<any>[] | undefined) | ProTableContextMenuGroup<any>[]
  showSummary?: boolean
  summaryMethod?: ProTableSummaryMethod<any>
  spanMethod?: ProTableSpanMethod<any>
}>(), {
  options: undefined,
  loading: false,
  rowKey: 'id',
  pagination: null,
  tools: undefined,
  storageKey: '',
  height: undefined,
  density: undefined,
  bordered: undefined,
  striped: undefined,
  showToolbar: undefined,
  showPagination: undefined,
  selection: false,
  indexColumn: false,
  batchActions: () => [],
  toolbarPluginNames: () => [],
  selectionWidth: undefined,
  indexWidth: undefined,
  treeProps: undefined,
  lazy: false,
  load: undefined,
  rowDraggable: false,
  rowDragHandle: '',
  rowContextMenu: undefined,
  showSummary: false,
  summaryMethod: undefined,
  spanMethod: undefined,
})

const emit = defineEmits<{
  refresh: []
  print: []
  pageChange: [page: number]
  sizeChange: [size: number]
  select: [payload: { selection: Record<string, any>[], row: Record<string, any> }]
  selectAll: [selection: Record<string, any>[]]
  selectionChange: [rows: any[]]
  currentChange: [payload: any]
  expandChange: [payload: any]
  filterChange: [filters: Record<string, Array<string | number | boolean>>]
  cellClick: [payload: any]
  cellDblclick: [payload: any]
  rowClick: [payload: any]
  rowDblclick: [payload: any]
  headerClick: [payload: { column: unknown, event: Event }]
  headerDragend: [payload: { newWidth: number, oldWidth: number, column: unknown, event: Event }]
  rowOrderChange: [payload: any]
  sortChange: [payload: TableSortPayload]
  settingsChange: [settings: ProTableSettings]
  columnsChange: [columns: ProTableColumn<any>[]]
  rowContextMenu: [payload: any]
}>()

const slots = defineSlots<{
  tableTop?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  beforeToolbar?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  toolbarLeft?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  actions?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  toolbarRight?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  afterToolbar?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  middle?: (props: { selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  empty?: () => any
  append?: () => any
  pageLeft?: (props: { pagination: ProTablePagination | null, selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  pageRight?: (props: { pagination: ProTablePagination | null, selectedRows: Record<string, any>[], visibleColumns: ProTableColumn[] }) => any
  [key: string]: ((props: any) => any) | undefined
}>()

interface TableSortPayload {
  column?: unknown
  prop?: string | null
  order?: 'ascending' | 'descending' | null
  sorts?: Array<{ prop?: string, order: 'ascending' | 'descending' }>
}

const appAccountStore = useAppAccountStore()
const rootRef = ref<HTMLElement>()
const tableRef = ref<VxeTableInstance>()
const importInputRef = ref<HTMLInputElement>()
const sortContainerRef = ref<HTMLElement>()
let sortInstance: Sortable | undefined
let rowSortInstance: Sortable | undefined
const VxeTable = VxeUITable.VxeTable
const selectedRows = ref<Record<string, any>[]>([])
const expandedRows = ref<Record<string, any>[]>([])
const treeExpandedRowKeys = ref<Array<string | number>>([])
const canUndo = shallowRef(false)
const canRedo = shallowRef(false)
const toast = useFaToast()
const proTableDefaults = inject(proTableDefaultsInjectionKey, defaultProTableDefaults)
const density = ref<ProTableDensity>(props.density ?? proTableDefaults.density)
const bordered = ref(props.bordered ?? proTableDefaults.bordered)
const striped = ref(props.striped ?? proTableDefaults.striped)
const settingsOpen = ref(false)
const contextMenuState = ref<{
  row: Record<string, any>
  x: number
  y: number
  items: ProTableContextMenuGroup[]
} | null>(null)
const columnVisibleMap = ref<Record<string, boolean>>({})
const columnOrder = ref<string[]>([])
const { getRenderPluginByName } = useProTableRenderPlugin()
const { getToolbarPlugins } = useProTableToolbar()
let tableExpose!: ProTableExpose
const RenderableContent = defineComponent({
  name: 'FaProTableRenderableContent',
  props: {
    content: {
      type: null,
      required: true,
    },
  },
  setup(componentProps) {
    return () => componentProps.content
  },
})

const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(rootRef)

const densityMap: Record<ProTableDensity, 'mini' | 'small' | 'medium'> = {
  compact: 'mini',
  default: 'small',
  comfortable: 'medium',
}

function isEnabledConfig<T extends { enabled?: boolean }>(config: false | T | undefined): config is T {
  return Boolean(config && config.enabled !== false)
}

function resolvePermission(codes: string | string[] | undefined, context: Omit<ProTablePermissionContext, 'code'>) {
  const permission = props.options?.permission
  if (!permission || !codes) {
    return { visible: true, disabled: false }
  }
  const codeList = Array.isArray(codes) ? codes : [codes]
  const states = codeList.map((code) => {
    const result = permission.check(code, { ...context, code })
    return typeof result === 'boolean'
      ? { visible: result, disabled: false }
      : {
          visible: result.visible !== false,
          disabled: result.disabled === true,
        }
  })
  const matched = permission.mode === 'some'
    ? states.some(state => state.visible)
    : states.every(state => state.visible)
  return {
    visible: matched,
    disabled: matched && states.some(state => state.disabled),
  }
}

function canShowTool(tool: ProTableTool) {
  return resolvePermission(props.options?.permission?.tools?.[tool], { target: 'tool', tool }).visible
}

function isToolDisabled(tool: ProTableTool) {
  if (resolvePermission(props.options?.permission?.tools?.[tool], { target: 'tool', tool }).disabled) {
    return true
  }
  if (tool === 'undo') {
    return !canUndo.value
  }
  if (tool === 'redo') {
    return !canRedo.value
  }
  return false
}

function containsEditableColumn(columns: ProTableColumn[]): boolean {
  return columns.some(column => Boolean(column.editor) || Boolean(column.children?.length && containsEditableColumn(column.children)))
}

const editingEnabled = computed(() => isEnabledConfig(props.options?.edit) && containsEditableColumn(props.columns))
const resolvedTools = computed(() => {
  const tools = [...(props.tools ?? proTableDefaults.tools)]
  const exportOptions = props.options?.export
  const importOptions = props.options?.import
  if (isEnabledConfig(importOptions) && importOptions.showToolbar !== false && !tools.includes('import')) {
    tools.push('import')
  }
  if (isEnabledConfig(exportOptions) && exportOptions.showToolbar !== false && !tools.includes('export')) {
    tools.push('export')
  }
  return tools.filter((tool) => {
    if (!canShowTool(tool)) {
      return false
    }
    if (['undo', 'redo'].includes(tool)) {
      return editingEnabled.value
    }
    if (tool === 'print') {
      return props.options?.print !== false && props.options?.print?.enabled !== false
    }
    return true
  })
})
const resolvedShowToolbar = computed(() => props.showToolbar ?? proTableDefaults.showToolbar)
const resolvedShowPagination = computed(() => props.showPagination ?? proTableDefaults.showPagination)
const resolvedSelectionWidth = computed(() => props.selectionWidth ?? proTableDefaults.selectionWidth)
const resolvedIndexWidth = computed(() => props.indexWidth ?? proTableDefaults.indexWidth)

const storageId = computed(() => {
  if (!props.storageKey) {
    return ''
  }
  return `fa-pro-table:${appAccountStore.account || 'guest'}:${props.storageKey}`
})

const normalizedColumns = computed(() => {
  const usedKeys = new Map<string, number>()
  const normalizeColumn = (column: ProTableColumn, path: number[]): ProTableColumn => {
    const baseKey = String(column.key ?? column.prop ?? `column_${path.join('_')}`)
    const occurrence = (usedKeys.get(baseKey) ?? 0) + 1
    usedKeys.set(baseKey, occurrence)

    return {
      ...column,
      type: column.type ?? 'default',
      key: occurrence === 1 ? baseKey : `${baseKey}__${occurrence}`,
      fixed: column.fixed ?? (column.type === 'selection' || column.type === 'index' ? 'left' : undefined),
      children: column.children?.map((child, childIndex) => normalizeColumn(child, [...path, childIndex])),
    }
  }

  const list = props.columns.map((column, index) => normalizeColumn(column, [index]))

  if (props.selection && !list.some(column => column.type === 'selection')) {
    list.unshift({
      key: '__selection__',
      type: 'selection',
      width: resolvedSelectionWidth.value,
      align: 'center',
      fixed: 'left',
    })
  }

  if (props.indexColumn && !list.some(column => column.type === 'index')) {
    const insertIndex = props.selection ? 1 : 0
    list.splice(insertIndex, 0, {
      key: '__index__',
      type: 'index',
      label: '#',
      width: resolvedIndexWidth.value,
      align: 'center',
      fixed: 'left',
    })
  }

  return list
})

function flattenColumns(columns: ProTableColumn[]): ProTableColumn[] {
  return columns.flatMap(column => [
    column,
    ...(column.children?.length ? flattenColumns(column.children) : []),
  ])
}

function getColumnKey(column: ProTableColumn) {
  return String(column.key ?? column.prop ?? '')
}

function getRowKeyValue(row: Record<string, any>) {
  if (typeof props.rowKey === 'function') {
    return props.rowKey(row)
  }
  return resolveValue(row, props.rowKey)
}

function traverseRows(rows: Record<string, any>[], handler: (row: Record<string, any>) => void) {
  const childrenKey = props.treeProps?.children ?? 'children'
  rows.forEach((row) => {
    handler(row)
    const children = row[childrenKey]
    if (Array.isArray(children) && children.length) {
      traverseRows(children, handler)
    }
  })
}

function findRowsByKeys(keys: Array<string | number>) {
  const matchedRows: Record<string, any>[] = []
  const keySet = new Set(keys.map(key => String(key)))
  traverseRows(props.data, (row) => {
    const key = getRowKeyValue(row)
    if (keySet.has(String(key))) {
      matchedRows.push(row)
    }
  })
  return matchedRows
}

const configurableColumns = computed(() => {
  return flattenColumns(normalizedColumns.value).filter(column => column.type === 'default' && !column.children?.length && column.label)
})

const visibleColumns = computed(() => {
  const filterVisibleColumns = (columns: ProTableColumn[]): ProTableColumn[] => {
    return columns.flatMap((column) => {
      const hideByFlag = column.hide === true || column.visible === false
      const hideByRender = typeof column.isRender === 'function'
        ? column.isRender() === false
        : column.isRender === false
      const hideByPermission = !resolvePermission(column.permissionCode, { target: 'column', column }).visible

      if (hideByFlag || hideByRender || hideByPermission) {
        return []
      }

      const nextChildren = column.children?.length
        ? filterVisibleColumns(column.children)
        : undefined

      if (column.children?.length && !nextChildren?.length) {
        return []
      }

      if (column.type === 'default' && !column.children?.length) {
        const columnVisible = columnVisibleMap.value[getColumnKey(column)] ?? true
        if (!columnVisible) {
          return []
        }
      }

      return [{
        ...column,
        children: nextChildren?.length ? nextChildren : undefined,
      }]
    })
  }

  const list = filterVisibleColumns(normalizedColumns.value)
  const defaultOrderMap = new Map(flattenColumns(normalizedColumns.value).map((column, index) => [getColumnKey(column), index]))
  const customOrderMap = new Map(columnOrder.value.map((key, index) => [key, index]))

  const fixedColumnRank = (column: ProTableColumn) => {
    if (column.type === 'selection') {
      return -3
    }
    if (column.type === 'index') {
      return -2
    }
    if (column.type === 'expand') {
      return -1
    }
    return 0
  }

  const getColumnOrder = (column: ProTableColumn): number => {
    const key = getColumnKey(column)
    const customOrder = customOrderMap.get(key)
    if (customOrder !== undefined) {
      return customOrder
    }

    if (column.children?.length) {
      return Math.min(...column.children.map(child => getColumnOrder(child)))
    }

    return defaultOrderMap.get(key) ?? 999
  }

  const sortColumns = (columns: ProTableColumn[]): ProTableColumn[] => {
    return [...columns]
      .map(column => ({
        ...column,
        children: column.children?.length ? sortColumns(column.children) : undefined,
      }))
      .sort((a, b) => {
        const rankDiff = fixedColumnRank(a) - fixedColumnRank(b)
        if (rankDiff !== 0) {
          return rankDiff
        }
        return getColumnOrder(a) - getColumnOrder(b)
      })
  }

  return sortColumns(list)
})

const orderedConfigurableColumns = computed(() => {
  const orderMap = new Map(columnOrder.value.map((key, index) => [key, index]))
  return [...configurableColumns.value].sort((a, b) => {
    const aOrder = orderMap.get(getColumnKey(a)) ?? Number.MAX_SAFE_INTEGER
    const bOrder = orderMap.get(getColumnKey(b)) ?? Number.MAX_SAFE_INTEGER
    return aOrder - bOrder
  })
})

const tableSize = computed(() => densityMap[density.value])
const flatVisibleColumns = computed(() => flattenColumns(visibleColumns.value).filter(column => !column.children?.length))
const tableStyle = computed<CSSProperties>(() => {
  const getDepth = (columns: ProTableColumn[], depth = 1): number => columns.reduce((maxDepth, column) => {
    const columnDepth = column.children?.length ? getDepth(column.children, depth + 1) : depth
    return Math.max(maxDepth, columnDepth)
  }, depth)

  return {
    '--fa-pro-table-header-rows': String(getDepth(visibleColumns.value)),
  }
})

function resolveVxeColumnField(column: ProTableColumn) {
  if (['selection', 'index', 'expand'].includes(column.type ?? 'default') || column.children?.length) {
    return undefined
  }
  const prop = String(column.prop ?? '')
  if (!prop) {
    return getColumnKey(column)
  }
  const duplicateCount = flatVisibleColumns.value.filter(item => String(item.prop ?? '') === prop).length
  return duplicateCount > 1 ? getColumnKey(column) : prop
}

function findColumnByVxeField(field: unknown) {
  return flatVisibleColumns.value.find(column => resolveVxeColumnField(column) === String(field ?? ''))
}

function resolveSourceColumnProp(field: unknown) {
  const column = findColumnByVxeField(field)
  return String(column?.prop ?? field ?? '')
}

const indexOffset = computed(() => props.pagination
  ? Math.max(0, (props.pagination.page - 1) * props.pagination.size)
  : 0)
const menuConfig = computed(() => props.rowContextMenu
  ? { body: { disabled: true } }
  : undefined)
const treeColumnKey = computed(() => {
  if (!props.treeProps) {
    return ''
  }
  const column = flatVisibleColumns.value.find(item => !['selection', 'index', 'expand'].includes(item.type ?? 'default'))
  return column ? getColumnKey(column) : ''
})
const rowDragColumnKey = computed(() => {
  if (!props.rowDraggable || props.rowDragHandle) {
    return ''
  }
  const column = flatVisibleColumns.value.find(item => !['selection', 'index', 'expand'].includes(item.type ?? 'default'))
  return column ? getColumnKey(column) : ''
})
const rowConfig = computed(() => ({
  keyField: typeof props.rowKey === 'string' ? props.rowKey : undefined,
  isCurrent: props.options?.interaction?.currentRow ?? true,
  isHover: props.options?.interaction?.hoverRow ?? true,
  drag: props.rowDraggable && !props.rowDragHandle,
}))
const checkboxConfig = computed(() => {
  const column = flatVisibleColumns.value.find(item => item.type === 'selection')
  return {
    reserve: column?.reserveSelection,
    checkStrictly: props.treeProps?.checkStrictly,
    checkMethod: column?.selectable
      ? ({ row }: { row: Record<string, any> }) => column.selectable?.(row, props.data.indexOf(row)) ?? true
      : undefined,
  }
})
const treeConfig = computed(() => props.treeProps
  ? {
      transform: props.treeProps.transform,
      rowField: props.treeProps.rowField ?? (typeof props.rowKey === 'string' ? props.rowKey : undefined),
      parentField: props.treeProps.parentField,
      childrenField: props.treeProps.children ?? 'children',
      mapChildrenField: props.treeProps.mapChildren,
      hasChildField: props.treeProps.hasChildren ?? 'hasChildren',
      lazy: props.lazy,
      reserve: true,
      loadMethod: props.load
        ? ({ row }: { row: Record<string, any> }) => new Promise<Record<string, any>[]>((resolve) => {
            props.load?.(row, {}, resolve)
          })
        : undefined,
    }
  : undefined)
const sortConfig = computed(() => ({
  remote: props.options?.sort?.remote ?? flatVisibleColumns.value.some(column => column.sortable === 'custom'),
  multiple: props.options?.sort?.multiple,
  chronological: props.options?.sort?.chronological,
  allowClear: props.options?.sort?.allowClear,
  trigger: props.options?.sort?.trigger,
}))
const filterConfig = computed(() => ({
  remote: props.options?.filter?.remote,
  multiple: props.options?.filter?.multiple,
}))
const columnConfig = computed(() => ({
  useKey: true,
  resizable: props.options?.interaction?.resizable ?? true,
}))
const keyboardConfig = computed(() => {
  const keyboard = props.options?.interaction?.keyboard
  if (!keyboard) {
    return undefined
  }
  const config = keyboard === true ? {} : keyboard
  return {
    isArrow: config.arrows ?? true,
    isTab: config.tab ?? true,
    isEnter: config.enter ?? true,
    isEsc: config.escape ?? true,
    isEdit: config.edit ?? true,
    isUndoRedo: editingEnabled.value,
  }
})

function resolveVirtualAxis(axis: boolean | ProTableVirtualScrollAxisOptions | undefined, direction: 'x' | 'y') {
  if (!axis) {
    return undefined
  }
  const config = axis === true ? {} : axis
  return {
    enabled: config.enabled ?? true,
    gt: config.gt ?? 0,
    oSize: config.overscan,
    preSize: config.preload,
    immediate: config.immediate,
    threshold: config.threshold,
    ...(direction === 'x'
      ? { scrollToLeftOnChange: config.resetOnChange }
      : { scrollToTopOnChange: config.resetOnChange }),
  }
}

const virtualXConfig = computed(() => resolveVirtualAxis(props.options?.virtualScroll && props.options.virtualScroll.x, 'x'))
const virtualYConfig = computed(() => resolveVirtualAxis(props.options?.virtualScroll && props.options.virtualScroll.y, 'y'))
const editConfig = computed(() => {
  const edit = props.options?.edit
  if (!isEnabledConfig(edit)) {
    return undefined
  }
  return {
    enabled: true,
    mode: edit.mode ?? 'cell',
    trigger: edit.trigger ?? 'dblclick',
    showStatus: edit.showStatus ?? true,
    beforeEditMethod: edit.beforeEdit
      ? ({ row, column, rowIndex, columnIndex }: any) => edit.beforeEdit?.({
          row,
          column: findColumnByVxeField(column.field),
          rowIndex,
          columnIndex,
        }) ?? true
      : undefined,
  }
})
const undoRedoHistoryConfig = computed(() => isEnabledConfig(props.options?.edit)
  ? { stackSize: props.options.edit.historySize ?? 30 }
  : undefined)
const vxeExportConfig = computed(() => isEnabledConfig(props.options?.export) ? toVxeExportOptions(props.options.export) : undefined)
const vxeImportConfig = computed(() => isEnabledConfig(props.options?.import) ? toVxeImportOptions(props.options.import) : undefined)
const vxePrintConfig = computed(() => isEnabledConfig(props.options?.print) ? toVxePrintOptions(props.options.print) : undefined)
const rowDragConfig = computed(() => props.rowDraggable && !props.rowDragHandle
  ? {
      trigger: 'default' as const,
      showIcon: true,
      animation: true,
    }
  : undefined)

function getDefaultSettings(): ProTableSettings {
  return {
    density: props.density ?? proTableDefaults.density,
    bordered: props.bordered ?? proTableDefaults.bordered,
    striped: props.striped ?? proTableDefaults.striped,
    columnVisibleMap: Object.fromEntries(
      configurableColumns.value.map(column => [getColumnKey(column), column.visible !== false]),
    ),
    columnOrder: configurableColumns.value.map(column => getColumnKey(column)),
  }
}

function applySettings(settings?: Partial<ProTableSettings>) {
  const defaults = getDefaultSettings()
  density.value = settings?.density ?? defaults.density
  bordered.value = settings?.bordered ?? defaults.bordered
  striped.value = settings?.striped ?? defaults.striped
  columnVisibleMap.value = {
    ...defaults.columnVisibleMap,
    ...(settings?.columnVisibleMap ?? {}),
  }
  columnOrder.value = settings?.columnOrder?.length ? settings.columnOrder : defaults.columnOrder
}

function loadSettings() {
  if (!storageId.value) {
    applySettings()
    return
  }
  try {
    const raw = localStorage.getItem(storageId.value)
    if (!raw) {
      applySettings()
      return
    }
    applySettings(JSON.parse(raw) as Partial<ProTableSettings>)
  }
  catch {
    applySettings()
  }
}

function persistSettings() {
  const nextSettings = {
    density: density.value,
    bordered: bordered.value,
    striped: striped.value,
    columnVisibleMap: columnVisibleMap.value,
    columnOrder: columnOrder.value,
  }
  if (storageId.value) {
    localStorage.setItem(storageId.value, JSON.stringify(nextSettings))
  }
  emit('settingsChange', nextSettings)
}

function resetSettings() {
  applySettings()
  persistSettings()
}

function getSettings(): ProTableSettings {
  return {
    density: density.value,
    bordered: bordered.value,
    striped: striped.value,
    columnVisibleMap: columnVisibleMap.value,
    columnOrder: columnOrder.value,
  }
}

function setSettings(settings: Partial<ProTableSettings>) {
  applySettings(settings)
  persistSettings()
}

function toggleColumnVisible(key: string, checked: boolean | 'indeterminate' | null | undefined) {
  columnVisibleMap.value = {
    ...columnVisibleMap.value,
    [key]: checked === true,
  }
  persistSettings()
}

function updateDensity(nextDensity: ProTableDensity) {
  density.value = nextDensity
  persistSettings()
}

function syncColumnOrder(newIndex: number, oldIndex: number) {
  const list = [...columnOrder.value]
  list.splice(newIndex, 0, list.splice(oldIndex, 1)[0])
  columnOrder.value = list
  persistSettings()
}

function handleSelectionChange(rows: Record<string, any>[]) {
  selectedRows.value = rows
  emit('selectionChange', rows)
}

function handleSelect(selection: Record<string, any>[], row: Record<string, any>) {
  emit('select', { selection, row })
}

function handleSelectAll(selection: Record<string, any>[]) {
  emit('selectAll', selection)
}

function handleRefresh() {
  emit('refresh')
}

function toVxeExportOptions(config: ProTableExportOptions = {}) {
  return {
    type: config.type ?? 'csv',
    types: config.types ?? ['csv', 'html', 'xml', 'txt'],
    filename: config.filename,
    sheetName: config.sheetName,
    isHeader: config.includeHeader ?? true,
    isFooter: config.includeFooter ?? false,
    original: config.original,
    data: config.onlySelected ? selectedRows.value : undefined,
    includeFields: config.includeFields?.map(String),
    excludeFields: config.excludeFields?.map(String),
    download: false,
    message: false,
  }
}

function toVxeImportOptions(config: ProTableImportOptions = {}) {
  return {
    types: config.types ?? ['csv', 'html', 'xml', 'txt'],
    mode: config.mode ?? 'covering',
  }
}

function toVxePrintOptions(config: ProTablePrintOptions = {}) {
  return {
    filename: config.filename,
    sheetName: config.sheetName,
    isHeader: config.includeHeader ?? true,
    isFooter: config.includeFooter ?? false,
    includeFields: config.includeFields?.map(String),
    excludeFields: config.excludeFields?.map(String),
    style: config.style,
  }
}

const exportMimeTypes: Record<string, string> = {
  csv: 'text/csv;charset=utf-8',
  html: 'text/html;charset=utf-8',
  xml: 'application/xml;charset=utf-8',
  txt: 'text/plain;charset=utf-8',
}

function resolveExportFilename(config: ProTableExportOptions, type: string) {
  const configuredName = typeof config.filename === 'function' ? config.filename() : config.filename
  const filename = configuredName || `export_${new Date().toISOString().slice(0, 10)}`
  return filename.toLowerCase().endsWith(`.${type}`) ? filename : `${filename}.${type}`
}

function downloadExportResult(result: any, config: ProTableExportOptions) {
  const type = String(result?.type || config.type || 'csv')
  const blob = result?.blob instanceof Blob
    ? result.blob
    : new Blob([result?.content ?? ''], { type: exportMimeTypes[type] || 'application/octet-stream' })
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = resolveExportFilename(config, type)
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

async function exportData(config: ProTableExportOptions = {}) {
  if (!tableRef.value?.exportData) {
    throw new Error('表格尚未完成初始化')
  }
  const result = await tableRef.value.exportData(toVxeExportOptions(config))
  if (result?.status === false) {
    throw new Error('表格数据导出失败')
  }
  downloadExportResult(result, config)
  return result
}

function importData(file: File, config: ProTableImportOptions = {}) {
  return tableRef.value?.importByFile(file, toVxeImportOptions(config)) ?? Promise.resolve()
}

async function printTable(config: ProTablePrintOptions = {}) {
  if (!tableRef.value?.print) {
    throw new Error('表格尚未完成初始化')
  }
  const result = await tableRef.value.print(toVxePrintOptions(config))
  if (result?.status === false) {
    throw new Error('表格打印失败')
  }
  return result
}

async function handleExport() {
  const config = isEnabledConfig(props.options?.export) ? props.options.export : {}
  try {
    await exportData(config)
    toast.success('导出成功')
  }
  catch (error) {
    toast.error(error instanceof Error ? error.message : '导出失败')
  }
}

function handleImportClick() {
  importInputRef.value?.click()
}

async function handleImportFile(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    const config = isEnabledConfig(props.options?.import) ? props.options.import : {}
    try {
      await importData(file, config)
      toast.success('导入成功')
    }
    catch (error) {
      toast.error(error instanceof Error ? error.message : '导入失败')
    }
  }
  input.value = ''
}

async function handlePrint() {
  emit('print')
  const config = isEnabledConfig(props.options?.print) ? props.options.print : {}
  try {
    await printTable(config)
  }
  catch (error) {
    toast.error(error instanceof Error ? error.message : '表格打印失败')
  }
}

function syncHistoryState() {
  const history = tableRef.value?.internalData?.stackHistoryStore
  canUndo.value = Boolean(history?.undoStacks.length)
  canRedo.value = Boolean(history?.redoStacks.length)
}

async function undo() {
  const result = await (tableRef.value?.undo() ?? Promise.resolve({ status: false }))
  syncHistoryState()
  return result
}

async function redo() {
  const result = await (tableRef.value?.redo() ?? Promise.resolve({ status: false }))
  syncHistoryState()
  return result
}

function insertRows(rows: Record<string, any> | Record<string, any>[], target?: Record<string, any> | -1 | null) {
  return tableRef.value?.insertAt(rows, target) ?? Promise.resolve({ row: undefined, rows: [] })
}

function removeRows(rows?: Record<string, any> | Record<string, any>[]) {
  return tableRef.value?.remove(rows) ?? Promise.resolve({ row: undefined, rows: [] })
}

function revertRows(rows?: Record<string, any> | Record<string, any>[], field?: string) {
  return tableRef.value?.revertData(rows, field) ?? Promise.resolve()
}

function getRecordset() {
  const recordset = tableRef.value?.getRecordset()
  return {
    insertRecords: recordset?.insertRecords ?? [],
    removeRecords: recordset?.removeRecords ?? [],
    updateRecords: recordset?.updateRecords ?? [],
  }
}

function activateEdit(row: Record<string, any>, field: string) {
  return tableRef.value?.setEditCell(row, field) ?? Promise.resolve()
}

function clearEdit() {
  return tableRef.value?.clearEdit() ?? Promise.resolve()
}

function clearSelection() {
  void tableRef.value?.clearCheckboxRow()
  void tableRef.value?.clearCheckboxReserve()
  handleSelectionChange([])
}

function clearFilter(columnKeys?: string[]) {
  const event = new Event('clear-filter')
  if (columnKeys?.length) {
    columnKeys.forEach(key => void tableRef.value?.clearFilterByEvent(event, key))
    return
  }
  void tableRef.value?.clearFilterByEvent(event)
}

function clearSort() {
  tableRef.value?.clearSort()
}

function sort(prop: string, order: 'ascending' | 'descending') {
  void tableRef.value?.sort(prop, order === 'ascending' ? 'asc' : 'desc')
}

function setCurrentRow(row?: Record<string, any>) {
  tableRef.value?.setCurrentRow(row)
}

function doLayout() {
  void tableRef.value?.recalculate(true)
}

function scrollTo(options: ScrollToOptions | number, yCoord?: number) {
  if (typeof options === 'number') {
    ;(tableRef.value as any)?.scrollTo(options, yCoord)
    return
  }
  ;(tableRef.value as any)?.scrollTo(options)
}

function getSelectedRows() {
  return selectedRows.value
}

function getExpandedRows() {
  return expandedRows.value
}

function toggleRowExpansion(row: Record<string, any>, expanded?: boolean) {
  void tableRef.value?.setRowExpand(row, expanded ?? true)
}

function toggleTreeRowExpansion(row: Record<string, any>, expanded?: boolean) {
  void tableRef.value?.setTreeExpand(row, expanded ?? true)
}

function expandAllRows() {
  void tableRef.value?.setAllRowExpand(true)
}

function collapseAllRows() {
  void tableRef.value?.setAllRowExpand(false)
}

function getTreeExpandedRowKeys() {
  return treeExpandedRowKeys.value
}

function setTreeExpandedRowKeys(keys: Array<string | number>) {
  const normalizedKeys = Array.from(new Set(keys.map(key => String(key))))
  const rows = findRowsByKeys(keys)

  traverseRows(props.data, (row) => {
    const rowKey = getRowKeyValue(row)
    void tableRef.value?.setTreeExpand(row, normalizedKeys.includes(String(rowKey)))
  })

  treeExpandedRowKeys.value = keys
  expandedRows.value = rows
}

function expandAllTreeRows() {
  const nextKeys: Array<string | number> = []
  const nextRows: Record<string, any>[] = []

  traverseRows(props.data, (row) => {
    const rowKey = getRowKeyValue(row)
    if (rowKey !== undefined && rowKey !== null) {
      nextKeys.push(rowKey as string | number)
      nextRows.push(row)
      void tableRef.value?.setTreeExpand(row, true)
    }
  })

  treeExpandedRowKeys.value = nextKeys
  expandedRows.value = nextRows
}

function collapseAllTreeRows() {
  void tableRef.value?.setAllTreeExpand(false)
  treeExpandedRowKeys.value = []
  expandedRows.value = []
}

function getVisibleColumns() {
  return visibleColumns.value
}

function getTreeProps() {
  return props.treeProps
}

function handleExpandChange(row: Record<string, any>, rowsOrExpanded: Record<string, any>[] | boolean) {
  const nextExpandedRows = Array.isArray(rowsOrExpanded)
    ? rowsOrExpanded
    : rowsOrExpanded
      ? [...expandedRows.value.filter(item => item !== row), row]
      : expandedRows.value.filter(item => item !== row)

  expandedRows.value = nextExpandedRows
  if (props.treeProps) {
    treeExpandedRowKeys.value = nextExpandedRows
      .map(item => getRowKeyValue(item))
      .filter((key): key is string | number => key !== undefined && key !== null)
  }
  emit('expandChange', {
    row,
    expandedRows: nextExpandedRows,
  })
}

function handleCurrentChange(currentRow?: Record<string, any> | null, oldCurrentRow?: Record<string, any> | null) {
  emit('currentChange', {
    currentRow: currentRow ?? undefined,
    oldCurrentRow: oldCurrentRow ?? undefined,
  })
}

function handleFilterChange(filters: Record<string, Array<string | number | boolean>>) {
  emit('filterChange', filters)
}

function handleCellClick(row: Record<string, any>, column: unknown, cell: HTMLTableCellElement, event: Event) {
  emit('cellClick', { row, column, cell, event })
}

function handleCellDblclick(row: Record<string, any>, column: unknown, cell: HTMLTableCellElement, event: Event) {
  emit('cellDblclick', { row, column, cell, event })
}

function handleRowClick(row: Record<string, any>, column: unknown, event: Event) {
  emit('rowClick', { row, column, event })
}

function handleRowDblclick(row: Record<string, any>, column: unknown, event: Event) {
  emit('rowDblclick', { row, column, event })
}

function handleHeaderClick(column: unknown, event: Event) {
  emit('headerClick', { column, event })
}

function handleHeaderDragend(newWidth: number, oldWidth: number, column: unknown, event: Event) {
  emit('headerDragend', { newWidth, oldWidth, column, event })
}

function syncRowSortable(newIndex: number, oldIndex: number) {
  if (newIndex === oldIndex) {
    return
  }
  const rows = [...props.data]
  const movedRow = rows[oldIndex]
  rows.splice(newIndex, 0, rows.splice(oldIndex, 1)[0])
  emit('rowOrderChange', {
    rows,
    movedRow,
    oldIndex,
    newIndex,
  })
}

function destroyRowSort() {
  rowSortInstance?.destroy()
  rowSortInstance = undefined
}

async function setupRowSort() {
  destroyRowSort()
  if (!props.rowDraggable || !props.rowDragHandle || props.loading) {
    return
  }
  await nextTick()
  const tableEl = tableRef.value?.$el as HTMLElement | undefined
  const tbody = tableEl?.querySelector('.vxe-table--main-wrapper .vxe-table--body-wrapper tbody') as HTMLElement | null
  if (!tbody) {
    return
  }
  rowSortInstance = new Sortable(tbody, {
    animation: 180,
    handle: props.rowDragHandle,
    draggable: 'tr',
    onUpdate: (event: SortableEvent) => {
      if (event.newIndex !== undefined && event.oldIndex !== undefined) {
        syncRowSortable(event.newIndex, event.oldIndex)
      }
    },
  })
}

function resolveSummaryMethod(param: { columns: unknown[], data: Record<string, any>[] }) {
  if (!props.summaryMethod) {
    return []
  }
  return [props.summaryMethod({
    columns: visibleColumns.value,
    data: param.data,
  }).map(item => typeof item === 'number' ? String(item) : item)]
}

function resolveSpanMethod(param: { row: Record<string, any>, column: unknown, rowIndex: number, columnIndex: number }) {
  if (!props.spanMethod) {
    return undefined
  }

  const currentColumn = flatVisibleColumns.value[param.columnIndex]
  if (!currentColumn) {
    return undefined
  }

  const span = props.spanMethod({
    row: param.row,
    column: currentColumn,
    rowIndex: param.rowIndex,
    columnIndex: param.columnIndex,
  })
  return Array.isArray(span) ? { rowspan: span[0], colspan: span[1] } : span
}

function resolveColumnSlotName(column: ProTableColumn) {
  if (column.slot === '__schema_actions__' && slots[column.slot]) {
    return column.slot
  }
  const rawKey = String(column.prop ?? column.key ?? '').replaceAll('.', '-')
  const standardName = rawKey ? `column-${rawKey}` : ''
  if (standardName && slots[standardName]) {
    return standardName
  }
  if (column.slot && slots[column.slot]) {
    return column.slot
  }
  return ''
}

function resolveHeaderSlotName(column: ProTableColumn) {
  if (column.renderHeader) {
    return ''
  }
  const rawKey = String(column.prop ?? column.key ?? '').replaceAll('.', '-')
  const standardName = rawKey ? `header-${rawKey}` : ''
  if (standardName && slots[standardName]) {
    return standardName
  }
  return ''
}

function resolveBatchDisabled(action: ProTableBatchAction) {
  if (resolvePermission(action.permissionCode, { target: 'batchAction', rows: selectedRows.value, source: action }).disabled) {
    return true
  }
  if (typeof action.disabled === 'function') {
    return action.disabled(selectedRows.value)
  }
  return action.disabled ?? false
}

function resolveBatchVisible(action: ProTableBatchAction) {
  if (!resolvePermission(action.permissionCode, { target: 'batchAction', rows: selectedRows.value, source: action }).visible) {
    return false
  }
  if (typeof action.visible === 'function') {
    return action.visible(selectedRows.value)
  }
  return action.visible ?? true
}

async function handleBatchAction(action: ProTableBatchAction) {
  await action.handle(selectedRows.value)
}

function handleSortChange(payload: TableSortPayload) {
  emit('sortChange', payload)
}

function mergeSelectedRows(records: Record<string, any>[], reserves: Record<string, any>[] = []) {
  return Array.from(new Set([...records, ...reserves]))
}

function handleVxeCheckboxChange(payload: any) {
  const selection = mergeSelectedRows(payload.records ?? [], payload.reserves ?? [])
  handleSelectionChange(selection)
  handleSelect(selection, payload.row)
}

function handleVxeCheckboxAll(payload: any) {
  const selection = mergeSelectedRows(payload.records ?? [], payload.reserves ?? [])
  handleSelectionChange(selection)
  handleSelectAll(selection)
}

function handleVxeCurrentChange(payload: any) {
  handleCurrentChange(payload.newValue, payload.oldValue)
}

function handleVxeExpandChange(payload: any) {
  const records = props.treeProps
    ? tableRef.value?.getTreeExpandRecords() ?? []
    : tableRef.value?.getRowExpandRecords() ?? []
  handleExpandChange(payload.row, records)
}

function handleVxeFilterChange(payload: any) {
  const filters = Object.fromEntries((payload.filterList ?? []).map((item: any) => [String(resolveSourceColumnProp(item.field)), item.values ?? []]))
  handleFilterChange(filters)
}

function handleVxeSortChange(payload: any) {
  handleSortChange({
    column: payload.column,
    prop: payload.field == null ? null : resolveSourceColumnProp(payload.field),
    order: payload.order === 'asc' ? 'ascending' : payload.order === 'desc' ? 'descending' : null,
    sorts: (payload.sortList ?? []).map((item: any) => ({
      prop: resolveSourceColumnProp(item.field),
      order: item.order === 'asc' ? 'ascending' : 'descending',
    })),
  })
}

function handleVxeCellClick(payload: any) {
  handleCellClick(payload.row, payload.column, payload.cell, payload.$event)
  handleRowClick(payload.row, payload.column, payload.$event)
}

function handleVxeCellDblclick(payload: any) {
  handleCellDblclick(payload.row, payload.column, payload.cell, payload.$event)
  handleRowDblclick(payload.row, payload.column, payload.$event)
}

function handleVxeHeaderClick(payload: any) {
  handleHeaderClick(payload.column, payload.$event)
}

function handleVxeResizableChange(payload: any) {
  const oldWidth = Number(payload.column?.renderWidth ?? payload.resizeWidth)
  handleHeaderDragend(payload.resizeWidth, oldWidth, payload.column, payload.$event)
}

function handleVxeRowDragend(payload: any) {
  if (props.treeProps?.transform) {
    emit('rowOrderChange', {
      rows: tableRef.value?.getFullData() ?? [],
      movedRow: payload.dragRow,
      oldIndex: payload._index?.oldIndex ?? -1,
      newIndex: payload._index?.newIndex ?? -1,
    })
    return
  }
  syncRowSortable(payload._index?.newIndex ?? -1, payload._index?.oldIndex ?? -1)
}

function handleVxeCellMenu(payload: any) {
  handleRowContextMenu(payload.row, payload.column, payload.$event)
}

function resolveValue(row: Record<string, any>, path?: string) {
  if (!path) {
    return ''
  }
  return path.split('.').reduce<unknown>((result, key) => {
    if (result == null || typeof result !== 'object') {
      return undefined
    }
    return (result as Record<string, unknown>)[key]
  }, row) ?? ''
}

function renderCellContent(scope: { row: Record<string, any>, $index: number }, column: ProTableColumn) {
  const value = resolveValue(scope.row, String(column.prop || ''))
  if (column.render) {
    return column.render({
      row: scope.row,
      column,
      value,
      $index: scope.$index,
    })
  }
  if (column.cellRenderTo) {
    const plugin = getRenderPluginByName(column.cellRenderTo.name)
    if (plugin) {
      return plugin.render({
        row: scope.row,
        column,
        value,
        $index: scope.$index,
      }, column.cellRenderTo.props, tableExpose)
    }
  }
  if (column.formatter) {
    return column.formatter(scope.row, scope.$index, column)
  }
  return value
}

function shouldRenderComplexContent(column: ProTableColumn) {
  return Boolean(column.render || column.cellRenderTo || column.formatter)
}

function resolvePlainCellContent(scope: { row: Record<string, any>, $index: number }, column: ProTableColumn) {
  if (column.type === 'expand') {
    return ''
  }
  return resolveValue(scope.row, String(column.prop || ''))
}

function resolveEditorDisabled(row: Record<string, any>, column: ProTableColumn, rowIndex: number) {
  const editor = typeof column.editor === 'object' ? column.editor : undefined
  if (resolvePermission(editor?.permissionCode, { target: 'edit', row, column }).disabled) {
    return true
  }
  if (editor?.permissionCode && !resolvePermission(editor.permissionCode, { target: 'edit', row, column }).visible) {
    return true
  }
  return typeof editor?.disabled === 'function'
    ? editor.disabled({ row, column, rowIndex })
    : editor?.disabled ?? false
}

function closeContextMenu() {
  contextMenuState.value = null
}

function resolveContextMenuItems(row: Record<string, any>) {
  const source = typeof props.rowContextMenu === 'function'
    ? props.rowContextMenu(row)
    : props.rowContextMenu

  if (!source?.length) {
    return []
  }

  return source
    .map(group => group.filter((item) => {
      if (!resolvePermission(item.permissionCode, { target: 'contextMenu', row, source: item }).visible) {
        return false
      }
      if (typeof item.visible === 'function') {
        return item.visible(row)
      }
      return item.visible !== false
    }))
    .filter(group => group.length > 0)
}

function resolveContextMenuDisabled(item: ProTableContextMenuItem, row: Record<string, any>) {
  if (resolvePermission(item.permissionCode, { target: 'contextMenu', row, source: item }).disabled) {
    return true
  }
  if (typeof item.disabled === 'function') {
    return item.disabled(row)
  }
  return item.disabled ?? false
}

async function handleContextMenuItemClick(item: ProTableContextMenuItem, row: Record<string, any>) {
  if (resolveContextMenuDisabled(item, row)) {
    return
  }
  await item.handle?.(row)
  closeContextMenu()
}

function handleRowContextMenu(row: Record<string, any>, _column: unknown, event: Event) {
  const mouseEvent = event as MouseEvent
  emit('rowContextMenu', { row, event: mouseEvent })

  const items = resolveContextMenuItems(row)
  if (!items.length) {
    return
  }

  mouseEvent.preventDefault()
  contextMenuState.value = {
    row,
    x: mouseEvent.clientX,
    y: mouseEvent.clientY,
    items,
  }
}

useEventListener(window, 'click', () => {
  closeContextMenu()
})

useEventListener(window, 'contextmenu', (event) => {
  if (!rootRef.value?.contains(event.target as Node)) {
    closeContextMenu()
  }
})

useEventListener(window, 'keydown', (event) => {
  if (event.key === 'Escape') {
    closeContextMenu()
  }
})

useEventListener(window, 'scroll', () => {
  closeContextMenu()
}, { capture: true })

const toolbarPlugins = computed<ProTableToolbarPlugin[]>(() => {
  const all = getToolbarPlugins()
  if (!props.toolbarPluginNames.length) {
    return all.filter((item: ProTableToolbarPlugin) => item.show !== false)
  }
  return all.filter((item: ProTableToolbarPlugin) => item.show !== false && props.toolbarPluginNames.includes(item.name))
})

const hasToolbar = computed(() => {
  return resolvedShowToolbar.value
    || Boolean(slots.toolbarLeft)
    || Boolean(slots.toolbarRight)
    || Boolean(props.batchActions.length)
    || Boolean(toolbarPlugins.value.length)
})

const toolbarContext = computed<ProTableToolbarContext>(() => ({
  selectedRows: selectedRows.value,
  visibleColumns: visibleColumns.value,
  refresh: handleRefresh,
  resetSettings,
  print: handlePrint,
  toggleFullscreen,
  isFullscreen: isFullscreen.value,
}))

tableExpose = {
  refresh: handleRefresh,
  clearSelection,
  clearFilter,
  clearSort,
  sort,
  setCurrentRow,
  doLayout,
  scrollTo,
  exportData,
  importData,
  printTable,
  undo,
  redo,
  insertRows,
  removeRows,
  revertRows,
  getRecordset,
  activateEdit,
  clearEdit,
  getSelectedRows,
  getExpandedRows,
  toggleRowExpansion,
  expandAllRows,
  collapseAllRows,
  getTreeExpandedRowKeys,
  setTreeExpandedRowKeys,
  toggleTreeRowExpansion,
  expandAllTreeRows,
  collapseAllTreeRows,
  resetSettings,
  print: handlePrint,
  getVisibleColumns,
  getSettings,
  setSettings,
  toggleFullscreen,
  getTreeProps,
  getTableRef: () => tableRef.value,
}

defineExpose(tableExpose)

watch(
  () => [props.storageKey, appAccountStore.account, props.columns, props.density, props.bordered, props.striped] as const,
  () => {
    loadSettings()
  },
  { deep: true, immediate: true },
)

watch(
  visibleColumns,
  (value) => {
    emit('columnsChange', value)
  },
  { deep: true, immediate: true },
)

watch(
  () => settingsOpen.value,
  async (open) => {
    if (sortInstance) {
      sortInstance.destroy()
      sortInstance = undefined
    }
    if (!open) {
      return
    }
    await nextTick()
    if (!sortContainerRef.value) {
      return
    }
    sortInstance = new Sortable(sortContainerRef.value, {
      animation: 180,
      handle: '.pro-table-column-drag',
      draggable: '.pro-table-column-item',
      onUpdate: (event: SortableEvent) => {
        if (event.newIndex !== undefined && event.oldIndex !== undefined) {
          syncColumnOrder(event.newIndex, event.oldIndex)
        }
      },
    })
  },
)

watch(
  () => [props.rowDraggable, props.rowDragHandle, props.data, props.loading] as const,
  () => {
    void setupRowSort()
  },
  { deep: true, immediate: true },
)

watch(
  () => props.data,
  () => {
    if (!props.treeProps) {
      return
    }
    const validKeys: Array<string | number> = []
    traverseRows(props.data, (row) => {
      const key = getRowKeyValue(row)
      if (key !== undefined && key !== null) {
        validKeys.push(key as string | number)
      }
    })
    const validKeySet = new Set(validKeys.map(key => String(key)))
    treeExpandedRowKeys.value = treeExpandedRowKeys.value.filter(key => validKeySet.has(String(key)))
    expandedRows.value = findRowsByKeys(treeExpandedRowKeys.value)
  },
  { deep: true, immediate: true },
)

onBeforeUnmount(() => {
  destroyRowSort()
})
</script>

<template>
  <div ref="rootRef" class="flex flex-col gap-4">
    <slot name="tableTop" :selected-rows="selectedRows" :visible-columns="visibleColumns" />
    <slot name="middle" :selected-rows="selectedRows" :visible-columns="visibleColumns" />

    <slot v-if="hasToolbar" name="beforeToolbar" :selected-rows="selectedRows" :visible-columns="visibleColumns" />
    <div v-if="hasToolbar" class="flex flex-wrap gap-3 items-center justify-between">
      <div class="flex flex-wrap gap-2 items-center">
        <div
          v-for="(action, index) in batchActions.filter(item => resolveBatchVisible(item))"
          :key="`${action.label}_${index}`"
        >
          <FaButton
            variant="outline"
            size="sm"
            :disabled="!selectedRows.length || resolveBatchDisabled(action)"
            @click="handleBatchAction(action)"
          >
            <FaIcon v-if="action.icon" :name="action.icon" />
            {{ action.label }}
          </FaButton>
        </div>
        <slot name="toolbarLeft" :selected-rows="selectedRows" :visible-columns="visibleColumns" />
      </div>

      <div class="flex flex-wrap gap-2 items-center">
        <slot name="actions" :selected-rows="selectedRows" :visible-columns="visibleColumns" />
        <slot name="toolbarRight" :selected-rows="selectedRows" :visible-columns="visibleColumns" />

        <RenderableContent
          v-for="plugin in toolbarPlugins"
          :key="plugin.name"
          :content="plugin.render(toolbarContext)"
        />

        <FaButton
          v-if="resolvedTools.includes('refresh')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="loading || isToolDisabled('refresh')"
          title="刷新"
          aria-label="刷新"
          @click="handleRefresh"
        >
          <FaIcon name="i-ri:refresh-line" :class="{ 'animate-spin': loading }" />
        </FaButton>

        <FaDropdown
          v-if="resolvedTools.includes('density')"
          :items="[
            [
              { label: '紧凑', icon: density === 'compact' ? 'i-ri:check-line' : undefined, handle: () => updateDensity('compact') },
              { label: '默认', icon: density === 'default' ? 'i-ri:check-line' : undefined, handle: () => updateDensity('default') },
              { label: '宽松', icon: density === 'comfortable' ? 'i-ri:check-line' : undefined, handle: () => updateDensity('comfortable') },
            ],
          ]"
          align="end"
        >
          <FaButton
            variant="outline"
            size="icon-sm"
            class="rounded-full"
            :disabled="isToolDisabled('density')"
            title="表格密度"
            aria-label="表格密度"
          >
            <FaIcon name="i-ri:line-height" />
          </FaButton>
        </FaDropdown>

        <FaButton
          v-if="resolvedTools.includes('fullscreen')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="isToolDisabled('fullscreen')"
          :title="isFullscreen ? '退出全屏' : '全屏'"
          :aria-label="isFullscreen ? '退出全屏' : '全屏'"
          @click="toggleFullscreen()"
        >
          <FaIcon :name="isFullscreen ? 'i-ri:fullscreen-exit-line' : 'i-ri:fullscreen-line'" />
        </FaButton>

        <input
          ref="importInputRef"
          type="file"
          class="hidden"
          accept=".csv,.html,.xml,.txt"
          @change="handleImportFile"
        >

        <FaButton
          v-if="resolvedTools.includes('import')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="isToolDisabled('import')"
          title="导入"
          aria-label="导入"
          @click="handleImportClick"
        >
          <FaIcon name="i-ri:upload-2-line" />
        </FaButton>

        <FaButton
          v-if="resolvedTools.includes('export')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="isToolDisabled('export')"
          title="导出"
          aria-label="导出"
          @click="handleExport"
        >
          <FaIcon name="i-ri:download-2-line" />
        </FaButton>

        <FaButton
          v-if="resolvedTools.includes('undo')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="isToolDisabled('undo')"
          title="撤销"
          aria-label="撤销"
          @click="undo"
        >
          <FaIcon name="i-ri:arrow-go-back-line" />
        </FaButton>

        <FaButton
          v-if="resolvedTools.includes('redo')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="isToolDisabled('redo')"
          title="重做"
          aria-label="重做"
          @click="redo"
        >
          <FaIcon name="i-ri:arrow-go-forward-line" />
        </FaButton>

        <FaButton
          v-if="resolvedTools.includes('print')"
          variant="outline"
          size="icon-sm"
          class="rounded-full"
          :disabled="isToolDisabled('print')"
          title="打印"
          aria-label="打印"
          @click="handlePrint"
        >
          <FaIcon name="i-ri:printer-line" />
        </FaButton>

        <FaPopover
          v-if="resolvedTools.includes('settings')"
          v-model:open="settingsOpen"
          align="end"
          class="p-0 w-84"
        >
          <FaButton
            variant="outline"
            size="icon-sm"
            class="rounded-full"
            :disabled="isToolDisabled('settings')"
            title="表格设置"
            aria-label="表格设置"
          >
            <FaIcon name="i-ri:settings-3-line" />
          </FaButton>

          <template #panel>
            <div class="px-4 py-3 border-b">
              <div class="text-sm font-medium">
                表格设置
              </div>
              <div class="text-xs text-muted-foreground mt-1">
                可统一控制显示列和基础展示样式。
              </div>
            </div>

            <div class="px-4 py-4 space-y-4">
              <div class="space-y-3">
                <div class="text-xs text-muted-foreground font-medium">
                  展示样式
                </div>
                <div class="flex gap-3 items-center justify-between">
                  <span class="text-sm">边框模式</span>
                  <FaSwitch v-model="bordered" @update:model-value="persistSettings" />
                </div>
                <div class="flex gap-3 items-center justify-between">
                  <span class="text-sm">斑马纹</span>
                  <FaSwitch v-model="striped" @update:model-value="persistSettings" />
                </div>
              </div>

              <div class="space-y-3">
                <div class="text-xs text-muted-foreground font-medium">
                  显示列
                </div>
                <div ref="sortContainerRef" class="pr-1 max-h-60 overflow-auto space-y-2">
                  <div
                    v-for="column in orderedConfigurableColumns"
                    :key="getColumnKey(column)"
                    class="pro-table-column-item px-3 py-2 border rounded-lg flex gap-3 items-center justify-between"
                  >
                    <div class="flex gap-3 items-center">
                      <FaIcon name="i-ri:draggable" class="pro-table-column-drag text-muted-foreground cursor-move" />
                      <span class="text-sm">{{ column.label }}</span>
                    </div>
                    <FaCheckbox
                      :model-value="columnVisibleMap[getColumnKey(column)] ?? true"
                      @update:model-value="toggleColumnVisible(getColumnKey(column), $event)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="px-4 py-3 border-t flex gap-2 items-center justify-end">
              <FaButton variant="outline" size="sm" @click="resetSettings">
                重置
              </FaButton>
              <FaButton size="sm" @click="settingsOpen = false">
                完成
              </FaButton>
            </div>
          </template>
        </FaPopover>
      </div>
    </div>
    <slot v-if="hasToolbar" name="afterToolbar" :selected-rows="selectedRows" :visible-columns="visibleColumns" />

    <VxeTable
      ref="tableRef"
      :data="data"
      :border="bordered"
      :stripe="striped"
      :size="tableSize"
      :height="height"
      :loading="loading && data.length === 0"
      :auto-resize="options?.interaction?.autoResize ?? true"
      :column-config="columnConfig"
      :row-config="rowConfig"
      :checkbox-config="checkboxConfig"
      :tree-config="treeConfig"
      :sort-config="sortConfig"
      :filter-config="filterConfig"
      :menu-config="menuConfig"
      :keyboard-config="keyboardConfig"
      :edit-config="editConfig"
      :keep-source="Boolean(editConfig)"
      :undo-redo-history-config="undoRedoHistoryConfig"
      :virtual-x-config="virtualXConfig"
      :virtual-y-config="virtualYConfig"
      :export-config="vxeExportConfig"
      :import-config="vxeImportConfig"
      :print-config="vxePrintConfig"
      :row-drag-config="rowDragConfig"
      :show-footer="showSummary"
      :footer-method="resolveSummaryMethod"
      :span-method="resolveSpanMethod"
      show-overflow="title"
      show-header-overflow="title"
      :style="tableStyle"
      :class="{
        'fa-pro-table__table--loading-empty': loading && data.length === 0,
        'fa-pro-table__table--refreshing': loading && data.length > 0,
      }"
      class="fa-pro-table__table w-full"
      @checkbox-change="handleVxeCheckboxChange"
      @checkbox-all="handleVxeCheckboxAll"
      @current-row-change="handleVxeCurrentChange"
      @toggle-row-expand="handleVxeExpandChange"
      @toggle-tree-expand="handleVxeExpandChange"
      @filter-change="handleVxeFilterChange"
      @sort-change="handleVxeSortChange"
      @cell-click="handleVxeCellClick"
      @cell-dblclick="handleVxeCellDblclick"
      @header-cell-click="handleVxeHeaderClick"
      @column-resizable-change="handleVxeResizableChange"
      @row-dragend="handleVxeRowDragend"
      @cell-menu="handleVxeCellMenu"
      @edit-activated="syncHistoryState"
    >
      <template v-if="slots.empty" #empty>
        <slot name="empty" />
      </template>

      <template #loading>
        <div class="fa-pro-table__loading" role="status" aria-live="polite">
          <div class="fa-pro-table__loading-visual" aria-hidden="true">
            <span class="fa-pro-table__loading-orbit" />
            <span class="fa-pro-table__loading-orbit fa-pro-table__loading-orbit--inner" />
            <span class="fa-pro-table__loading-core">
              <FaIcon name="i-ri:database-2-line" />
            </span>
          </div>
          <div class="fa-pro-table__loading-copy">
            <span class="fa-pro-table__loading-title">正在同步数据</span>
            <span class="fa-pro-table__loading-detail">正在更新当前视图，请稍候</span>
          </div>
          <span class="fa-pro-table__loading-signal" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </div>
      </template>

      <FaProTableRecursiveColumn
        v-for="column in visibleColumns"
        :key="getColumnKey(column)"
        :column="column"
        :slots-map="slots"
        :renderable-content="RenderableContent"
        :resolve-header-slot-name="resolveHeaderSlotName"
        :resolve-column-slot-name="resolveColumnSlotName"
        :should-render-complex-content="shouldRenderComplexContent"
        :render-cell-content="renderCellContent"
        :resolve-plain-cell-content="resolvePlainCellContent"
        :resolve-editor-disabled="resolveEditorDisabled"
        :resolve-column-field="resolveVxeColumnField"
        :tree-column-key="treeColumnKey"
        :row-drag-column-key="rowDragColumnKey"
        :index-offset="indexOffset"
      />
    </VxeTable>

    <slot v-if="slots.append" name="append" />

    <div v-if="pagination && resolvedShowPagination" class="flex flex-wrap gap-3 items-center justify-between">
      <div class="flex gap-3 items-center">
        <slot name="pageLeft" :pagination="pagination" :selected-rows="selectedRows" :visible-columns="visibleColumns" />
      </div>

      <div class="flex gap-3 items-center">
        <slot name="pageRight" :pagination="pagination" :selected-rows="selectedRows" :visible-columns="visibleColumns" />
        <FaPagination
          :page="pagination.page"
          :size="pagination.size"
          :total="pagination.total"
          @page-change="emit('pageChange', $event)"
          @size-change="emit('sizeChange', $event)"
        />
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="contextMenuState"
        class="p-1 border rounded-xl bg-popover min-w-44 shadow-lg fixed z-3000"
        :style="{ left: `${contextMenuState.x}px`, top: `${contextMenuState.y}px` }"
        @click.stop
      >
        <template v-for="(group, groupIndex) in contextMenuState.items" :key="groupIndex">
          <button
            v-for="(item, itemIndex) in group"
            :key="`${item.label}_${itemIndex}`"
            type="button"
            class="text-sm px-3 py-2 text-left rounded-lg flex gap-2 w-full transition items-center hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="resolveContextMenuDisabled(item, contextMenuState.row)"
            @click="handleContextMenuItemClick(item, contextMenuState.row)"
          >
            <FaIcon v-if="item.icon" :name="item.icon" class="shrink-0 size-4" />
            <span class="truncate">{{ item.label }}</span>
          </button>
          <div
            v-if="groupIndex < contextMenuState.items.length - 1"
            class="my-1 bg-border h-px"
          />
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
:deep(.fa-pro-table__table) {
  --fa-pro-table-current-row-height: var(--vxe-ui-table-row-height-small);
  --fa-pro-table-header-height: calc(var(--fa-pro-table-current-row-height) * var(--fa-pro-table-header-rows));
  --fa-pro-table-surface-muted: oklch(var(--muted));
  --fa-pro-table-row-hover: oklch(var(--accent));
  --fa-pro-table-row-current: oklch(var(--accent));
  --fa-pro-table-border: oklch(var(--border));
  --fa-pro-table-header-text: oklch(var(--muted-foreground));
  --vxe-ui-font-color: oklch(var(--foreground));
  --vxe-ui-font-primary-color: oklch(var(--primary));
  --vxe-ui-layout-background-color: oklch(var(--background));
  --vxe-ui-table-border-color: var(--fa-pro-table-border);
  --vxe-ui-table-header-background-color: var(--fa-pro-table-surface-muted);
  --vxe-ui-table-header-font-color: var(--fa-pro-table-header-text);
  --vxe-ui-table-row-hover-background-color: var(--fa-pro-table-row-hover);
  --vxe-ui-table-row-hover-striped-background-color: var(--fa-pro-table-row-hover);
  --vxe-ui-table-row-striped-background-color: var(--fa-pro-table-surface-muted);
  --vxe-ui-table-row-current-background-color: var(--fa-pro-table-row-current);
  --vxe-ui-table-row-hover-current-background-color: var(--fa-pro-table-row-current);
  --vxe-ui-table-header-font-weight: 600;
}

:deep(.fa-pro-table__table.size--mini) {
  --fa-pro-table-current-row-height: var(--vxe-ui-table-row-height-mini);
}

:deep(.fa-pro-table__table.size--medium) {
  --fa-pro-table-current-row-height: var(--vxe-ui-table-row-height-medium);
}

:deep(.fa-pro-table__table .vxe-loading--custom-wrapper) {
  top: var(--fa-pro-table-header-height);
  z-index: 4;
  display: none;
  align-items: center;
  justify-content: center;
  height: calc(100% - var(--fa-pro-table-header-height));
  overflow: hidden;
  pointer-events: none;
  background-color: color-mix(in oklch, oklch(var(--background)) 74%, transparent);
  backdrop-filter: blur(2px);
}

:deep(.fa-pro-table__table .vxe-loading--custom-wrapper.is--visible) {
  display: flex;
}

:deep(.fa-pro-table__table .vxe-loading--custom-wrapper.is--visible::before) {
  position: absolute;
  z-index: 0;
  width: 100%;
  height: 1px;
  content: "";
  background: linear-gradient(90deg, transparent 0%, color-mix(in oklch, oklch(var(--primary)) 55%, transparent) 48%, transparent 100%);
  animation: fa-pro-table-scan 2.4s ease-in-out infinite;
}

:deep(.fa-pro-table__table--refreshing::after) {
  position: absolute;
  top: var(--fa-pro-table-header-height);
  left: 0;
  z-index: 8;
  width: 28%;
  height: 2px;
  pointer-events: none;
  content: "";
  background: linear-gradient(90deg, transparent, oklch(var(--primary)), transparent);
  animation: fa-pro-table-refresh 1.35s ease-in-out infinite;
}

:deep(.fa-pro-table__table--loading-empty .vxe-table--body-wrapper) {
  min-height: 8rem;
}

.fa-pro-table__loading {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 3.5rem minmax(0, 1fr) auto;
  gap: 0.875rem;
  align-items: center;
  min-width: min(22rem, calc(100% - 2rem));
  padding: 0.875rem 1rem;
  overflow: hidden;
  color: oklch(var(--foreground));
  background:
    linear-gradient(135deg, color-mix(in oklch, oklch(var(--primary)) 8%, transparent), transparent 48%),
    color-mix(in oklch, oklch(var(--background)) 92%, transparent);
  border: 1px solid color-mix(in oklch, oklch(var(--primary)) 24%, oklch(var(--border)));
  border-radius: 0.5rem;
  box-shadow:
    0 16px 36px color-mix(in oklch, oklch(var(--foreground)) 10%, transparent),
    inset 0 1px color-mix(in oklch, white 55%, transparent);
}

.fa-pro-table__loading::after {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 4rem;
  height: 1px;
  content: "";
  background: linear-gradient(90deg, transparent, oklch(var(--primary)));
}

.fa-pro-table__loading-visual {
  position: relative;
  display: grid;
  place-items: center;
  width: 3.5rem;
  height: 3.5rem;
}

.fa-pro-table__loading-orbit {
  position: absolute;
  inset: 0;
  border: 1px solid color-mix(in oklch, oklch(var(--primary)) 22%, transparent);
  border-top-color: oklch(var(--primary));
  border-radius: 50%;
  animation: fa-pro-table-orbit 1.4s linear infinite;
}

.fa-pro-table__loading-orbit--inner {
  inset: 0.45rem;
  border-top-color: transparent;
  border-right-color: color-mix(in oklch, oklch(var(--primary)) 72%, white);
  animation-duration: 1.05s;
  animation-direction: reverse;
}

.fa-pro-table__loading-core {
  display: grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  color: oklch(var(--primary));
  background: color-mix(in oklch, oklch(var(--primary)) 10%, oklch(var(--background)));
  border-radius: 50%;
  box-shadow: 0 0 1.25rem color-mix(in oklch, oklch(var(--primary)) 22%, transparent);
}

.fa-pro-table__loading-copy {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  text-align: left;
}

.fa-pro-table__loading-title {
  font-size: 0.875rem;
  font-weight: 600;
}

.fa-pro-table__loading-detail {
  font-size: 0.75rem;
  color: oklch(var(--muted-foreground));
}

.fa-pro-table__loading-signal {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.fa-pro-table__loading-signal i {
  width: 0.25rem;
  height: 0.25rem;
  background: oklch(var(--primary));
  border-radius: 50%;
  animation: fa-pro-table-signal 1.2s ease-in-out infinite;
}

.fa-pro-table__loading-signal i:nth-child(2) {
  animation-delay: 0.15s;
}

.fa-pro-table__loading-signal i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes fa-pro-table-orbit {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fa-pro-table-scan {
  0%,
  100% {
    top: 16%;
    opacity: 0;
  }

  20%,
  80% {
    opacity: 1;
  }

  50% {
    top: 84%;
  }
}

@keyframes fa-pro-table-refresh {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(460%);
  }
}

@keyframes fa-pro-table-signal {
  0%,
  100% {
    opacity: 0.28;
    transform: translateY(0);
  }

  50% {
    opacity: 1;
    transform: translateY(-0.2rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  :deep(.fa-pro-table__table .vxe-loading--custom-wrapper.is--visible::before),
  :deep(.fa-pro-table__table--refreshing::after),
  .fa-pro-table__loading-orbit,
  .fa-pro-table__loading-signal i {
    animation: none;
  }
}
</style>
